import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { apiFetch } from '../../../utils';

import './index.css';

const AdminPanelComponent = () => {
    const currentUser = useSelector(state => state?.session?.user);

    const [newsRows, setNewsRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(8);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    const [newTitle, setNewTitle] = useState('');
    const [newText, setNewText] = useState('');
    const [savingNew, setSavingNew] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editText, setEditText] = useState('');
    const [editDate, setEditDate] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const toSqlDateTime = (date = new Date()) => {
        const pad = (value) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const loadNews = async (targetPage = page) => {
        setLoading(true);
        setErrors([]);

        const response = await apiFetch('/user/admin/news/list', 'POST', {
            page: targetPage,
            pageSize,
        });

        if (!response || response.code !== 'success') {
            const err = response?.data?.msg || ['Failed to load admin news data.'];
            setErrors(Array.isArray(err) ? err : ['Failed to load admin news data.']);
            setNewsRows([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const data = response.data || {};
        setNewsRows(Array.isArray(data.results) ? data.results : []);
        setTotal(data.total || 0);
        setTotalPages(Math.max(data.total_pages || 1, 1));
        setLoading(false);
    };

    useEffect(() => {
        if (!currentUser?.is_admin) return;
        loadNews(page);
    }, [page, currentUser?.is_admin]);

    const onCreateNews = async (e) => {
        e.preventDefault();
        setSavingNew(true);
        setErrors([]);

        const response = await apiFetch('/user/admin/news/create', 'POST', {
            title: newTitle,
            text: newText,
            date: toSqlDateTime(),
        });

        if (!response || response.code !== 'success') {
            const err = response?.data?.msg || ['Failed to create news post.'];
            setErrors(Array.isArray(err) ? err : ['Failed to create news post.']);
            setSavingNew(false);
            return;
        }

        setNewTitle('');
        setNewText('');
        setSavingNew(false);
        setPage(1);
        await loadNews(1);
    };

    const beginEdit = (row) => {
        setEditingId(row.id);
        setEditTitle(row.title || '');
        setEditText(row.text || '');
        setEditDate(row.date || '');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditTitle('');
        setEditText('');
        setEditDate('');
    };

    const saveEdit = async () => {
        if (!editingId) return;
        setSavingEdit(true);
        setErrors([]);

        const response = await apiFetch('/user/admin/news/update', 'POST', {
            id: editingId,
            title: editTitle,
            text: editText,
            date: editDate,
        });

        if (!response || response.code !== 'success') {
            const err = response?.data?.msg || ['Failed to update news post.'];
            setErrors(Array.isArray(err) ? err : ['Failed to update news post.']);
            setSavingEdit(false);
            return;
        }

        setSavingEdit(false);
        cancelEdit();
        await loadNews(page);
    };

    const deleteRow = async (id) => {
        if (!window.confirm('Delete this news post?')) return;
        setErrors([]);

        const response = await apiFetch('/user/admin/news/delete', 'POST', { id });
        if (!response || response.code !== 'success') {
            const err = response?.data?.msg || ['Failed to delete news post.'];
            setErrors(Array.isArray(err) ? err : ['Failed to delete news post.']);
            return;
        }

        await loadNews(page);
    };

    if (!currentUser?.is_admin) {
        return (
            <div id='admin-panel-container'>
                <div id='admin-panel-shell'>
                    <h2>Admin Panel</h2>
                    <p id='admin-panel-denied'>Admin access required.</p>
                </div>
            </div>
        );
    }

    return (
        <div id='admin-panel-container'>
            <div id='admin-panel-shell'>
                <h2>Admin Panel</h2>
                <p id='admin-panel-subtitle'>News Manager</p>

                {errors.map((error) => <p key={error} className='admin-panel-error'>{error}</p>)}

                <form id='admin-panel-create-form' onSubmit={onCreateNews}>
                    <h3>Create News Post</h3>
                    <input
                        className='admin-panel-input'
                        type='text'
                        maxLength={80}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder='Title (max 80)'
                        required
                    />
                    <textarea
                        className='admin-panel-textarea'
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        placeholder='News text'
                        required
                    />
                    <button className='button admin-panel-action-button' type='submit' disabled={savingNew}>
                        {savingNew ? 'Saving...' : 'Create'}
                    </button>
                </form>

                <div id='admin-panel-table-wrap'>
                    <table id='admin-panel-table'>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Title</th>
                                <th>Text</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan='5' className='admin-panel-empty'>Loading news...</td>
                                </tr>
                            )}

                            {!loading && newsRows.length === 0 && (
                                <tr>
                                    <td colSpan='5' className='admin-panel-empty'>No news posts found.</td>
                                </tr>
                            )}

                            {newsRows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td>
                                        {editingId === row.id ? (
                                            <input
                                                className='admin-panel-inline-input'
                                                type='text'
                                                maxLength={80}
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                            />
                                        ) : row.title}
                                    </td>
                                    <td>
                                        {editingId === row.id ? (
                                            <textarea
                                                className='admin-panel-inline-textarea'
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                            />
                                        ) : row.text}
                                    </td>
                                    <td>
                                        {row.date}
                                    </td>
                                    <td>
                                        <div className='admin-panel-actions'>
                                            {editingId === row.id ? (
                                                <>
                                                    <button className='button admin-panel-mini-button' type='button' onClick={saveEdit} disabled={savingEdit}>Save</button>
                                                    <button className='button admin-panel-mini-button admin-panel-cancel' type='button' onClick={cancelEdit}>Cancel</button>
                                                </>
                                            ) : (
                                                <>
                                                    <button className='button admin-panel-mini-button' type='button' onClick={() => beginEdit(row)}>Edit</button>
                                                    <button className='button admin-panel-mini-button admin-panel-delete' type='button' onClick={() => deleteRow(row.id)}>Delete</button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div id='admin-panel-pagination'>
                    <button
                        className='button admin-panel-action-button'
                        type='button'
                        disabled={loading || page <= 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                        Previous
                    </button>
                    <p>Page {page} / {totalPages} ({total.toLocaleString()} total)</p>
                    <button
                        className='button admin-panel-action-button'
                        type='button'
                        disabled={loading || page >= totalPages}
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminPanelComponent;
